import { useId, useRef, useState, type ReactNode } from "react";
import { BookOpen, ChevronUp, Volume2 } from "lucide-react";
import type { ContextKind, ContextLink } from "@bindery/shared/corpus/document";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@bindery/ui/components/ui/sheet";
import { Switch } from "@bindery/ui/components/ui/switch";
import { cn } from "@bindery/ui/lib/utils";
import { ContextExcerpt } from "./context-excerpt";
import { ReferenceChoiceCards } from "./reference-choice-cards";
import {
  preferredPronunciation,
  type DictionaryEntry,
  type LookupResource,
  type ReferenceLookupState,
  type ReferencePronunciation,
} from "./reference-lookup";

type OpenState = Extract<ReferenceLookupState, { status: "open" }>;

export interface ReferenceLookupCommands {
  close: () => void;
  select: (id: number) => void;
  check: () => "correct" | "wrong" | null;
  showDictionary: () => void;
  showContext: () => void;
  replay: (id?: string) => void;
  retryPronunciations: () => void;
  setAutoplay: (enabled: boolean) => void;
}

export interface ReferenceLookupDrawerProps extends ReferenceLookupCommands {
  state: ReferenceLookupState;
  /** The reader expanded the excerpt behind a context link. */
  onReadMore?: (link: ContextLink) => void;
  /** Where focus goes when the drawer closes. */
  onCloseAutoFocus?: (event: Event) => void;
}

const KIND_LABELS: Record<ContextKind, string> = {
  custom: "A custom",
  institution: "An institution",
  belief: "A belief",
  ritual: "A ritual",
  object: "An object",
  place: "A place",
  convention: "How the story is told",
};

const HARD_MISS = 2;

export function ReferenceLookupDrawer({
  state,
  onReadMore,
  onCloseAutoFocus,
  close,
  ...commands
}: ReferenceLookupDrawerProps) {
  // The last open state stays rendered through the close animation, and each
  // opening remounts the body so its local state starts fresh.
  const [opening, setOpening] = useState<{
    state: OpenState | null;
    key: number;
  }>({ state: null, key: 0 });
  if (state.status === "open" && opening.state !== state) {
    setOpening({
      state,
      key:
        opening.state?.selection === state.selection
          ? opening.key
          : opening.key + 1,
    });
  }
  const shown = state.status === "open" ? state : opening.state;

  return (
    <Sheet
      open={state.status === "open"}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        onCloseAutoFocus={onCloseAutoFocus}
        className={cn(
          "bg-popover text-popover-foreground max-h-[88svh] gap-0 rounded-t-2xl p-0 shadow-[0_-8px_40px_rgba(0,0,0,0.12)]",
          "md:inset-auto md:top-1/2 md:left-1/2 md:w-[30rem] md:max-w-[calc(100vw-2rem)] md:max-h-[85vh] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:border md:shadow-xl",
          "md:data-[state=closed]:slide-out-to-bottom-4 md:data-[state=open]:slide-in-from-bottom-4",
        )}
      >
        {shown && (
          <OpeningView
            key={opening.key}
            state={shown}
            onReadMore={readMoreHandler(shown, onReadMore)}
            {...commands}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function readMoreHandler(
  state: OpenState,
  onReadMore: ((link: ContextLink) => void) | undefined,
): (() => void) | undefined {
  const link = state.selection.context;
  if (!link || !onReadMore) return undefined;
  return () => onReadMore(link);
}

function OpeningView({
  state,
  onReadMore,
  select,
  check,
  showDictionary,
  showContext,
  replay,
  retryPronunciations,
  setAutoplay,
}: Omit<ReferenceLookupCommands, "close"> & {
  state: OpenState;
  onReadMore: (() => void) | undefined;
}) {
  const [announcement, setAnnouncement] = useState<{
    text: string;
    slot: 0 | 1;
  } | null>(null);
  const [focusAnswer, setFocusAnswer] = useState(false);
  const { selection, gate } = state;

  const announce = (text: string): void => {
    setAnnouncement((prev) => ({ text, slot: prev?.slot === 0 ? 1 : 0 }));
  };

  const onCheck = (): void => {
    const result = check();
    if (result === "correct") {
      announce("That's right.");
      setFocusAnswer(true);
    } else if (result === "wrong") {
      announce("Not quite — try again");
    }
  };

  return (
    <>
      <div className="sr-only">
        <output aria-live="polite">
          {announcement?.slot === 0 ? announcement.text : ""}
        </output>
        <output aria-live="polite">
          {announcement?.slot === 1 ? announcement.text : ""}
        </output>
      </div>
      {gate.status === "matching" ? (
        <MatchingStage
          word={selection.text}
          lang={selection.language}
          gate={gate}
          onSelect={select}
          onCheck={onCheck}
        />
      ) : (
        <RevealStage
          state={state}
          focusOnMount={focusAnswer}
          onReadMore={onReadMore}
          showDictionary={showDictionary}
          showContext={showContext}
          replay={replay}
          retryPronunciations={retryPronunciations}
          setAutoplay={setAutoplay}
        />
      )}
    </>
  );
}

function MatchingStage({
  word,
  lang,
  gate,
  onSelect,
  onCheck,
}: {
  word: string;
  lang: string;
  gate: Extract<OpenState["gate"], { status: "matching" }>;
  onSelect: (id: number) => void;
  onCheck: () => void;
}) {
  const canCheck = gate.selectedId !== null && gate.phase === "idle";
  return (
    <div className="flex flex-col items-center px-6 pt-12 pb-10 text-center md:pt-14">
      <Headword word={word} lang={lang} />
      <p className="text-muted-foreground mt-2 max-w-xs font-serif text-base leading-snug italic">
        {gate.wrongCount >= HARD_MISS
          ? "Slow down. Compare each letter to the word above."
          : "Look at each letter. Which one matches?"}
      </p>
      <div className="mt-9 w-full">
        <ReferenceChoiceCards
          gate={gate}
          word={word}
          lang={lang}
          onSelect={onSelect}
        />
      </div>
      <button
        type="button"
        aria-disabled={!canCheck}
        onClick={() => {
          if (canCheck) onCheck();
        }}
        className={cn(
          "mt-9 inline-flex min-h-12 min-w-28 items-center justify-center rounded-full px-6 font-serif text-base tracking-wide transition-colors duration-150",
          "focus-visible:ring-ring/60 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          canCheck
            ? "bg-lookup text-popover hover:bg-lookup/90"
            : "border-rule text-muted-foreground cursor-default border border-dotted",
        )}
      >
        Check
      </button>
    </div>
  );
}

function Headword({ word, lang }: { word: string; lang: string }) {
  return (
    <SheetTitle
      lang={lang}
      className="text-foreground font-serif text-4xl font-medium tracking-tight text-balance md:text-5xl"
    >
      {word}
    </SheetTitle>
  );
}

function RevealStage({
  state,
  focusOnMount,
  onReadMore,
  showDictionary,
  showContext,
  replay,
  retryPronunciations,
  setAutoplay,
}: Omit<ReferenceLookupCommands, "close" | "select" | "check"> & {
  state: OpenState;
  /** Move focus here on mount: the reader just matched the word. */
  focusOnMount: boolean;
  onReadMore: (() => void) | undefined;
}) {
  const focused = useRef(false);
  const autoplayId = useId();
  const { selection, answer, pronunciations, playback } = state;
  const ready = pronunciations.status === "ready" ? pronunciations.value : [];
  const preferred = preferredPronunciation(ready) ?? ready[0];
  const canPlay = ready.some((p) => p.audioUrl !== null);
  const hasContext = selection.context !== null;

  return (
    <div
      ref={(el) => {
        if (el && focusOnMount && !focused.current) {
          focused.current = true;
          el.focus({ preventScroll: true });
        }
      }}
      tabIndex={-1}
      className="flex min-h-0 flex-col focus:outline-none"
    >
      <div className="shrink-0 px-6 pt-8 pr-14 text-center md:pt-10">
        <div className="flex items-baseline justify-center gap-3">
          <Headword word={selection.text} lang={selection.language} />
          {canPlay && (
            <ReplayButton playing={playback === "playing"} onReplay={replay} />
          )}
        </div>
        {preferred && (
          <p className="text-muted-foreground mt-1.5 font-serif text-base leading-snug">
            {preferred.respelling && (
              <span className="text-foreground/80 mr-2 tracking-wide">
                {preferred.respelling}
              </span>
            )}
            <span className="italic">{preferred.label}</span>
          </p>
        )}
        {playback === "error" && (
          <p className="text-muted-foreground mt-1.5 font-serif text-sm">
            The recording couldn’t play. Press the speaker to try again.
          </p>
        )}
        {pronunciations.status === "error" && (
          <p className="text-muted-foreground mt-1.5 font-serif text-sm">
            Couldn’t load the pronunciation.{" "}
            <TextButton onClick={retryPronunciations}>Try again</TextButton>
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6 pb-8">
        {answer?.kind === "context" ? (
          <ContextAnswer
            link={answer.link}
            headword={selection.text}
            onReadMore={onReadMore}
            onShowDictionary={showDictionary}
          />
        ) : (
          <DictionaryAnswer
            entries={answer?.kind === "dictionary" ? answer.entries : null}
            onRetry={showDictionary}
            onShowContext={hasContext ? showContext : undefined}
          />
        )}

        {ready.length > 0 && (
          <PronunciationDetails
            pronunciations={ready}
            playingId={state.playingId}
            onPlay={replay}
          />
        )}

        <div className="border-rule text-muted-foreground mt-8 flex items-center justify-between gap-4 border-t border-dotted pt-4 font-serif text-sm">
          <label htmlFor={autoplayId}>Say it out loud when this opens</label>
          <Switch
            id={autoplayId}
            checked={state.autoplay}
            onCheckedChange={setAutoplay}
            size="sm"
            className="data-[state=checked]:bg-lookup"
          />
        </div>
      </div>
    </div>
  );
}

function ReplayButton({
  playing,
  onReplay,
}: {
  playing: boolean;
  onReplay: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onReplay()}
      aria-label={playing ? "Playing. Replay" : "Replay"}
      className={cn(
        "border-lookup/40 text-lookup hover:bg-lookup/10 inline-flex min-h-10 shrink-0 translate-y-1 items-center gap-1.5 rounded-full border pr-4 pl-3 font-serif text-sm tracking-wide transition-colors",
        "focus-visible:ring-ring/60 focus-visible:ring-2 focus-visible:outline-none",
      )}
    >
      <Volume2
        aria-hidden
        className={cn("size-5", playing && "motion-safe:animate-pulse")}
      />
      {playing ? "Playing" : "Replay"}
    </button>
  );
}

function Rubric({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-smcp text-muted-foreground shrink-0 text-[13px] tracking-[0.219em] lowercase">
        {children}
      </span>
      <span
        aria-hidden="true"
        className="border-rule h-0 flex-1 border-b border-dotted"
      />
    </div>
  );
}

function TextButton({
  onClick,
  children,
  ...rest
}: {
  onClick: () => void;
  children: ReactNode;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-lookup hover:decoration-lookup focus-visible:ring-ring/60 inline-flex min-h-8 items-center gap-1.5 rounded-sm font-serif text-sm underline decoration-dotted underline-offset-[0.22em] focus-visible:ring-2 focus-visible:outline-none"
      {...rest}
    >
      {children}
    </button>
  );
}

function ContextAnswer({
  link,
  headword,
  onReadMore,
  onShowDictionary,
}: {
  link: ContextLink;
  headword: string;
  onReadMore: (() => void) | undefined;
  onShowDictionary: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const excerptId = useId();
  const toggle = (): void => {
    if (!expanded) onReadMore?.();
    setExpanded(!expanded);
  };
  return (
    <section aria-label="Background">
      <Rubric>{KIND_LABELS[link.kind]}</Rubric>
      {link.concept.toLocaleLowerCase() !== headword.toLocaleLowerCase() && (
        <h3 className="text-foreground mt-3 font-serif text-xl leading-snug font-bold tracking-tight">
          {link.concept}
        </h3>
      )}
      <p className="text-foreground/90 mt-3 font-serif text-base leading-relaxed">
        {link.gloss}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <TextButton
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={expanded ? excerptId : undefined}
        >
          {expanded ? (
            <ChevronUp aria-hidden className="size-4" />
          ) : (
            <BookOpen aria-hidden className="size-4" />
          )}
          {expanded ? "Show less" : "Read more"}
        </TextButton>
        <TextButton onClick={onShowDictionary}>Dictionary meaning</TextButton>
      </div>
      {expanded && (
        <div
          id={excerptId}
          className="border-rule mt-4 border-t border-dotted pt-4"
        >
          <ContextExcerpt link={link} />
        </div>
      )}
    </section>
  );
}

function DictionaryAnswer({
  entries,
  onRetry,
  onShowContext,
}: {
  entries: LookupResource<readonly DictionaryEntry[]> | null;
  onRetry: () => void;
  onShowContext: (() => void) | undefined;
}) {
  return (
    <section aria-label="Dictionary">
      <Rubric>Dictionary</Rubric>
      <div className="mt-3">
        <DictionaryBody entries={entries} onRetry={onRetry} />
      </div>
      {onShowContext && (
        <div className="mt-3">
          <TextButton onClick={onShowContext}>Back to context</TextButton>
        </div>
      )}
    </section>
  );
}

function DictionaryBody({
  entries,
  onRetry,
}: {
  entries: LookupResource<readonly DictionaryEntry[]> | null;
  onRetry: () => void;
}) {
  const status = entries?.status ?? "idle";
  if (status === "idle" || status === "loading") {
    return (
      <p
        aria-busy="true"
        className="text-muted-foreground font-serif text-base italic"
      >
        Looking this up…
      </p>
    );
  }
  if (status === "missing") {
    return (
      <p className="text-muted-foreground font-serif text-base">
        No dictionary entry found
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="text-muted-foreground font-serif text-base">
        Couldn’t load the dictionary entry.{" "}
        <TextButton onClick={onRetry}>Try again</TextButton>
      </p>
    );
  }
  if (entries?.status !== "ready") return null;
  const bySource = new Map<string, DictionaryEntry[]>();
  for (const entry of entries.value) {
    const group = bySource.get(entry.source);
    if (group) group.push(entry);
    else bySource.set(entry.source, [entry]);
  }
  return (
    <div className="space-y-6">
      {[...bySource].map(([source, group]) => (
        <div key={source} className="space-y-4">
          {group.map((entry) => (
            <DictionaryEntryView key={entry.id} entry={entry} />
          ))}
          <p className="text-muted-foreground font-serif text-xs">
            From{" "}
            {group[0]?.sourceUrl ? (
              <a
                href={group[0].sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-lookup decoration-rule underline underline-offset-2"
              >
                {source}
              </a>
            ) : (
              source
            )}
          </p>
        </div>
      ))}
    </div>
  );
}

function DictionaryEntryView({ entry }: { entry: DictionaryEntry }) {
  return (
    <article>
      <p className="text-foreground font-serif text-base leading-snug">
        <span className="font-bold">{entry.headword}</span>
        {entry.partOfSpeech && (
          <span className="text-muted-foreground ml-2 italic">
            {entry.partOfSpeech}
          </span>
        )}
      </p>
      <ol className="text-foreground/90 mt-1.5 list-decimal space-y-1 pl-5 font-serif text-base leading-relaxed">
        {entry.senses.map((sense, i) => (
          <li key={i}>{sense}</li>
        ))}
      </ol>
    </article>
  );
}

function PronunciationDetails({
  pronunciations,
  playingId,
  onPlay,
}: {
  pronunciations: readonly ReferencePronunciation[];
  playingId: string | null;
  onPlay: (id: string) => void;
}) {
  return (
    <details className="group mt-8">
      <summary
        aria-label="Ways to say it"
        className="hover:text-foreground focus-visible:ring-ring/60 cursor-pointer list-none rounded-sm focus-visible:ring-2 focus-visible:outline-none"
      >
        <Rubric>Ways to say it</Rubric>
      </summary>
      <ul className="mt-3 space-y-3">
        {pronunciations.map((p) => (
          <li key={p.id} className="flex items-start gap-3">
            {p.audioUrl ? (
              <button
                type="button"
                onClick={() => onPlay(p.id)}
                aria-label={`Play ${p.label}`}
                className="text-lookup hover:bg-lookup/10 focus-visible:ring-ring/60 inline-flex size-9 shrink-0 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:outline-none"
              >
                <Volume2
                  aria-hidden
                  className={cn(
                    "size-5",
                    playingId === p.id && "motion-safe:animate-pulse",
                  )}
                />
              </button>
            ) : (
              <span aria-hidden className="size-9 shrink-0" />
            )}
            <div className="min-w-0 font-serif text-base leading-snug">
              <p className="text-foreground">
                {p.label}
                {p.respelling && (
                  <span className="text-foreground/80 ml-2 tracking-wide">
                    {p.respelling}
                  </span>
                )}
              </p>
              {p.ipa && (
                <p className="text-muted-foreground mt-0.5 text-sm">{p.ipa}</p>
              )}
              {!p.audioUrl && (
                <p className="text-muted-foreground mt-0.5 text-sm italic">
                  No recording
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}
